import type { SectionView } from "./SectionViewSwitcher";

// ---------------------------------------------------------------------------
// Pure URL-param helpers backing `useSectionState`. Readers derive state from
// `URLSearchParams`; writers mutate a (cloned) `URLSearchParams` in place. All
// branching (delete-vs-set, CSV join, page reset) lives here so the hook stays
// a thin orchestrator. Defaults are omitted from the URL.
// ---------------------------------------------------------------------------

type Filters = Record<string, readonly string[]>;

/** Parse CSV multi-value filters from the URL, falling back to defaults per key. */
export function parseFilters<TFilters extends Filters>(
    params: URLSearchParams,
    defaultFilters: TFilters,
): TFilters {
    const out = { ...defaultFilters } as { [K in keyof TFilters]: readonly string[] };
    for (const key of Object.keys(defaultFilters) as (keyof TFilters & string)[]) {
        const raw = params.get(key);
        if (raw) {
            out[key] = raw.split(",").filter(Boolean);
        }
    }
    return out as TFilters;
}

/** Total number of selected filter values across all dimensions. */
export function countActiveFilters(filters: Filters): number {
    let n = 0;
    for (const key of Object.keys(filters)) {
        const vals = filters[key];
        n += vals ? vals.length : 0;
    }
    return n;
}

export function writeTab(p: URLSearchParams, next: string, defaultTab: string): void {
    if (next === defaultTab) p.delete("tab");
    else p.set("tab", next);
    p.delete("page");
}

export function writeView(p: URLSearchParams, next: SectionView, defaultView: SectionView): void {
    if (next === defaultView) p.delete("view");
    else p.set("view", next);
}

export function writeQuery(p: URLSearchParams, next: string): void {
    if (next.trim() === "") p.delete("q");
    else p.set("q", next);
    p.delete("page");
}

export function writeFilter(p: URLSearchParams, key: string, values: readonly string[]): void {
    if (values.length === 0) p.delete(key);
    else p.set(key, values.join(","));
    p.delete("page");
}

export function writeFilters(p: URLSearchParams, entries: Partial<Record<string, readonly string[]>>): void {
    for (const [key, values] of Object.entries(entries)) {
        if (!values || values.length === 0) p.delete(key);
        else p.set(key, values.join(","));
    }
    p.delete("page");
}

export function clearFilters(p: URLSearchParams, defaultFilters: Filters): void {
    for (const key of Object.keys(defaultFilters)) p.delete(key);
    p.delete("page");
}

export function writeSelecting(p: URLSearchParams, on: boolean): void {
    if (on) p.set("select", "1");
    else p.delete("select");
}

export function writePage(p: URLSearchParams, next: number): void {
    if (next <= 1) p.delete("page");
    else p.set("page", String(next));
}
